import { store } from '@redux/store';
import GoogleAnalyticsCustomDimensions from 'constants/googleAnalyticsCustomDimension';
import GoogleAnalyticsEvent from 'dataTransferObjects/googleAnalyticsEvent';
import { userProfileActions } from 'features/userProfile';
import { selectModal } from 'layout/redux/selectors';
import { backButton, privacyPolicyParagraph } from '../constants/sharedHtml';
import leadConstructor from '../services/leadConstructor';
import { postLeadBrochureForm } from '../services/taskFlow';
import CommentsTextArea from './commentsTextArea';
import ContactRadioGroup from './contactRadioGroup';
import EmailInput from './emailInput';
import InformationButton from './informationButton';
import NameInput from './nameInput';
import PhoneInput from './phoneInput';
import ZipInput from './zipInput';
import LegalText from './legalText';

/* Creates a new instance of a Limited Availability Form to allow users to get more information on a lisiting. */
export default class BrochureFormLimitedAvailability {
  constructor() {
    const state = store.getState();
    const userProfile = state?.userProfile || {};
    const { id, guid, type } = selectModal(state);
    this.id = id;
    this.guid = guid;
    this.type = type;
    this.excludeRecommendations = false;
    this.isPersistent = true;
    this.moreInformationButton = new InformationButton();
    this.commentsTextArea = new CommentsTextArea();
    this.emailAddressInput = new EmailInput();
    this.emailAddressInput.value = userProfile.email;
    this.formId = 'limitedAvailabilityForm';
    this.fullNameInput = new NameInput();
    this.fullNameInput.value = userProfile.name;
    this.contactRadioGroup = new ContactRadioGroup();
    this.contactRadioGroup.value = userProfile.preferredContactMethod;
    this.postalCodeInput = new ZipInput();
    this.postalCodeInput.value = userProfile.zip;
    this.postalCodeInput.isInternational = userProfile.livesOutside;
    this.phoneNumberInput = new PhoneInput();
    this.phoneNumberInput.value = userProfile.phone;
    this.onClickEvent = null;
    this.legalText = new LegalText(privacyPolicyParagraph);
  }

  /**
   * validateNode: Check if the field has a value as expected.
   * @param {Object} node Input to validate
   * @returns A boolean value indicating whether the input is valid or not.
   */
  static validateNode(node) {
    return node.validate();
  }

  /**
   * getFormOpenedAnalyticsData: Gets the object required for the Google Analytics events on form opened.
   * @param {Object} properties Information regarding to the listing.
   * @returns The Google Analtic event information.
   */
  static getFormOpenedAnalyticsData(properties) {
    const customDimensions = {};

    if (properties.marketId) {
      customDimensions[[GoogleAnalyticsCustomDimensions.MarketId().getName()]] = properties.marketId;
    }

    if (properties.marketName && properties.stateAbbreviation) {
      customDimensions[[GoogleAnalyticsCustomDimensions.MarketName().getName()]] = `${properties.marketName}, ${properties.stateAbbreviation}`;
    }

    if (properties.city) {
      customDimensions[[GoogleAnalyticsCustomDimensions.City().getName()]] = properties.city;
    }
    return new GoogleAnalyticsEvent(
      "Lead_Events",
      "Search_Results_CTA_Free_Brochure",
      "Open_Form_Free_Brochure",
      customDimensions);
  }

  /**
   * setHousingData: Sets the housing information for the form.
   * @param {Object} properties Information regarding to the listing.
   */
  setHousingData(properties = {}) {
    this.housingData = properties;
  }

  /**
   * submitBrochureForm: Process to validate and submit the form.
   * @param {event} event Click event.
   */
  async submitBrochureForm(event) {
    event.preventDefault();

    const validationFunctions = [
      this.emailAddressInput,
      this.fullNameInput,
      this.postalCodeInput,
      this.phoneNumberInput,
    ];

    this.moreInformationButton.getNode().disabled = 'disabled';

    const results = await Promise.all(
      validationFunctions.map((input) => BrochureFormLimitedAvailability.validateNode(input))
    );

    if (results.some((isValid) => !isValid)) {
      this.moreInformationButton.getNode().disabled = false;
      return;
    }

    const formData = new FormData(this.getForm());
    const brochureInformation = Object.fromEntries(formData.entries());

    brochureInformation.id = this.id;
    brochureInformation.type = `${this.type}-brochure`;
    brochureInformation.guid = this.guid;

    const brochureLeadObject = leadConstructor(brochureInformation);
    brochureLeadObject.setHousingInformation?.(this.housingData.listingInformation);

    postLeadBrochureForm(brochureLeadObject);
  }

  /**
   * handleEmailValidationResult: Handles the result of the email validation.
   * @param {Object} isValid The result of the email validation.
   */
    handleEmailValidationResult(isValid) {
      this.moreInformationButton.getNode().disabled = false;
      if (isValid) {
        this.emailAddressInput.setValidState(this.emailAddressInput.getNode());
        this.emailAddressInput.toggleEmailValidationDataAttribute(true);
      } else {
        this.emailAddressInput.setInvalidState(this.emailAddressInput.getNode());
        this.emailAddressInput.toggleEmailValidationDataAttribute(false);
      }
    }

  /**
   * Sets the binding required for the form.
   */
  bindEvents() {
    this.onClickEvent = this.submitBrochureForm.bind(this);
    this.phoneNumberInput.bindEvents();
    this.postalCodeInput.bindEvents();
    this.legalText.bindEvents();
    this.moreInformationButton.getNode().addEventListener('click', this.onClickEvent);
    this.contactRadioGroup.onChange(this.onPreferredContactMethodChange.bind(this));

    const userProfile = store.getState()?.userProfile || {};
    this.setRequiredAttributeInPhone(userProfile.preferredContactMethod);
    this.bindChangeEvents();
  }

  /**
   * bindChangeEvents: Get the form inputs then subscribe each OnChangeEvent to store and persist their values
   */
  bindChangeEvents() {
    const changeInputs = [
      this.emailAddressInput,
      this.fullNameInput,
      this.postalCodeInput,
      this.phoneNumberInput,
    ];

    changeInputs.forEach(input => input.getOnChangeEvent().subscribe(data =>
      store.dispatch(userProfileActions.update(data))));
  }

  /**
   * Handles the change contact radio group event.
   * @param {event} event Change event.
   */
  onPreferredContactMethodChange(event) {
    const value = event.target.value;
    this.setRequiredAttributeInPhone(value);
  }

  /**
   * Sets the phone number input as required when it applies.
   * @param {string} value Contact option selected.
   */
  setRequiredAttributeInPhone(value) {
    if (value === 'Phone' || value === 'Text Message') {
      this.phoneNumberInput.setRequired();
      return;
    }

    this.phoneNumberInput.removeRequired();
  }

  /**
   * Removes the bindings that should not be still using.
   */
  unbindEvents() {
    this.moreInformationButton.getNode().removeEventListener('click', this.onClickEvent);
    this.legalText.unbindEvents();
  }

  /**
   * getForm: Gets the form container
   * @returns A HTML element related to the form container.
   */
  getForm() {
    return document.getElementById(this.formId);
  }

  /**
   * Gets the HTML structure for the limited availability brochure form paid.
   * @returns The string with the HTML structure.
   */
  getHtml() {
    this.legalText.setReplacements([
      { key: "{0}", value: "Get More Information"},
      { key: "{1}", value: this.housingData.listingInformation?.brandName ?? "the builder" },
    ]);
    return `
    <div class="lead-form form--brochure" data-lead-form data-qa="lead_modal">
      ${this.isPersistent ? backButton : ``}
      <p class="form__title">Limited Availability at this Community!</p>
      <p class="form__subtitle">
      <span>Don't miss out! Get info and updates.</span>
       You'll be put on the list for updates, and you can ask other questions about availability.</p>
      <form id='${this.formId}'>
        <fieldset>
          <div class="form__field">
            ${this.fullNameInput.getHtml()}
          </div>
          <div class="form__field">
            ${this.emailAddressInput.getHtml()}
          </div>
          <div class="form__field">
            ${this.phoneNumberInput.getHtml()}
          </div>
          <div class="form__field field--split">
            ${this.postalCodeInput.getHtml()}
          </div>
          <div class="form__field field--radiogroup" aria-labeledby="preferred-label">
            <span class="form__label" id="preferred-label">Preferred Contact Method</span>
            <div>
              ${this.contactRadioGroup.getHtml()}
            </div>
         </div>
         <div class="form__field">
            ${this.commentsTextArea.getHtml()}
          </div>
          <div class="form__button-bar">
            ${this.moreInformationButton.getHtml()}
          </div>
          <input type="hidden" id="leadAction" name="leadAction" value="fb" />
        </fieldset>        
      </form>
      ${this.legalText.getHtml()}
    </div>`;
  }
}
